"use client";

import { useState } from "react";
import { todayNZ } from "@aluume/shared/lib/format";

// Lightweight month calendar popup. Dates are plain YYYY-MM-DD strings; past days
// are disabled. No external dependency.
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function ymd(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// The actual month grid. Owns its own view (current month) state so it can be
// reused both as a fixed overlay and as an inline block.
function CalendarCard({
  value,
  onSelect,
  onClose,
  min,
}: {
  value: string;
  onSelect: (d: string) => void;
  onClose: () => void;
  /** Earliest selectable date (YYYY-MM-DD); defaults to today. Thermae passes its opening date pre-launch. */
  min?: string;
}) {
  const today = todayNZ();
  const floor = min && min > today ? min : today;
  const [view, setView] = useState(() => {
    const [y, m] = value.split("-").map(Number);
    return { y, m: m - 1 };
  });

  const first = new Date(Date.UTC(view.y, view.m, 1));
  const startDow = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate();
  const cells: (number | null)[] = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const prevMonth = () => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }));
  const nextMonth = () => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }));

  return (
    <div className="w-full max-w-[360px] rounded-2xl bg-cream p-6 shadow-[0px_24px_60px_-20px_rgba(57,51,48,0.35)]" onClick={(e) => e.stopPropagation()}>
      <div className="mb-5 flex items-center justify-between">
        <button onClick={prevMonth} className="flex size-9 items-center justify-center rounded-pill border border-ink/20 text-ink">←</button>
        <span style={{ fontFamily: "var(--font-display)" }} className="text-[20px] text-ink">{MONTHS[view.m]} {view.y}</span>
        <button onClick={nextMonth} className="flex size-9 items-center justify-center rounded-pill border border-ink/20 text-ink">→</button>
      </div>
      <div className="mb-2 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => <span key={i} className="text-[11px] uppercase tracking-[1px] text-ink-soft">{w}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <span key={i} />;
          const ds = ymd(view.y, view.m, d);
          const past = ds < floor;
          const sel = ds === value;
          return (
            <button
              key={i}
              disabled={past}
              onClick={() => { onSelect(ds); onClose(); }}
              className={`flex aspect-square items-center justify-center rounded-lg text-[14px] transition-colors ${
                sel ? "bg-ink text-ink-on-dark" : past ? "text-ink/25" : "text-ink hover:bg-cream-deep"
              }`}
            >
              {d}
            </button>
          );
        })}
      </div>
      <button onClick={onClose} className="ui-label mt-5 w-full rounded-pill border border-ink/20 py-2.5 text-ink">Close</button>
    </div>
  );
}

export function Calendar({
  value,
  onSelect,
  onClose,
  min,
  backdropClassName = "bg-ink/40",
  inline = false,
}: {
  value: string;
  onSelect: (d: string) => void;
  onClose: () => void;
  min?: string;
  // Override the full-screen scrim. In the Squarespace embed the iframe is a small
  // window, so a dark scrim would render as a translucent box on the host page —
  // the embed passes a transparent backdrop to avoid that.
  backdropClassName?: string;
  // Inline mode renders the calendar as a normal block (no fixed overlay), so a
  // host iframe grows to fit it instead of clipping a centered overlay. Used by
  // the Squarespace embed pill; everything else uses the default fixed overlay.
  inline?: boolean;
}) {
  if (inline) {
    return (
      <div className="mt-3 flex w-full justify-center">
        <CalendarCard value={value} onSelect={onSelect} onClose={onClose} min={min} />
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${backdropClassName}`} onClick={onClose}>
      <CalendarCard value={value} onSelect={onSelect} onClose={onClose} min={min} />
    </div>
  );
}
